import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  MarketingProfileById,
  MarketingProfileByIdSettings,
  OrganizationProfilesResponse,
  OrgDataResponse
} from '../@types/Marketing';
import { OrgByIdProps } from '../components/Marketing/components/OrgOwnership';
import { useAxios } from '../context/AxiosContext';
import { downloadFileFromResponse } from '../utils/fileDownload';

export enum ProfileType {
  ORGANIZATION = 'organization',
  PROFILE = 'profile'
}

export const PROFILE_CACHE_KEY = 'profileById';

export const minCharactersToFetch = 10;

interface DeleteMarketingProfileRes {
  success?: string;
  error?: string;
}
export interface MarketingProfile {
  id: string;
  name: string;
  default: boolean | string;
  affectedOrganizationsCount: number;
  createdAt: string;
  createdBy: {
    fullName: string;
  };
  updatedAt: string;
  updatedBy: {
    fullName: string;
  };
}

interface MarketingProfilesSettings {
  name: string;
  mode: string;
  sortBy: string;
  endCursor: string | null;
}

export type GetAllMarketingProfilesSettings = Partial<MarketingProfilesSettings>;

export interface GetAllMarketingProfilesRes {
  profiles: { node: MarketingProfile }[];
  endCursor: string | null;
  hasNextPage: boolean;
}

export interface GetAllMarketingProfilesData {
  pages: GetAllMarketingProfilesRes[];
  pageParams: any[];
}

interface AssignedProfileRes {
  error: string;
  profile: {
    id: string;
    name: string;
  };
}

interface MarketingMutationSettings {
  settings: MarketingProfileByIdSettings;
  baseFlg: boolean;
  name: string;
}

export interface MarketingSaveAsArgs extends MarketingMutationSettings {
  ownership: OrgByIdProps;
}

export interface MarketingMutationArgs extends MarketingMutationSettings {
  id: string;
}

export function useMarketingProfileAffectedGroups(id: string, type: ProfileType) {
  const { authAxios } = useAxios();

  const getAffectedGroupIds = useCallback(
    async (id, type): Promise<number> => {
      const { data } = await authAxios.get<Record<string, number>>('/marketing/affected-groups', {
        params: { type, id }
      });
      return data.affectedGroups;
    },
    [authAxios]
  );

  return useQuery(['MarketingProfileAffectedGroups', id, type], () => getAffectedGroupIds(id, type));
}

export function useMarketingProfileAvailableOwners(organizationId: string) {
  const { authAxios } = useAxios();

  const getAvailableOwners = useCallback(
    async (organizationId: string) => {
      const { data } = await authAxios.get<OrgDataResponse>('/marketing/ownership', { params: { organizationId } });
      return data;
    },
    [authAxios]
  );
  return useQuery(['marketingProfileAvailableOwners', organizationId], () => getAvailableOwners(organizationId));
}

export function useMarketingProfileName({ search = '' }: { search: string }) {
  const { authAxios } = useAxios();

  const checkProfileNameAvailability = useCallback(
    async (name: string) => {
      const { data } = await authAxios.get<{ availability: boolean }>('/marketing/validate-name', {
        params: { name }
      });
      return data.availability;
    },
    [authAxios]
  );

  return useQuery(['marketingProfileNameCheck', search], () => checkProfileNameAvailability(search), {
    refetchOnWindowFocus: false,
    initialData: false,
    enabled: search.length >= minCharactersToFetch,
    keepPreviousData: true
  });
}

export function useDownloadAffectedGroups() {
  const { authAxios } = useAxios();
  return (id: string, type: ProfileType, profileName?: string) => {
    authAxios
      .get('/marketing/download-affected-groups', {
        params: { id, type },
        responseType: 'blob'
      })
      .then((data) =>
        downloadFileFromResponse(data, `${profileName ? profileName.replace(/ /g, '_') : ''}AffectedGroups.csv`)
      );
  };
}

export function useOrganizationProfiles(id: string) {
  const { authAxios } = useAxios();

  const getOrganizationProfiles = useCallback(
    async (id: string) => {
      const { data } = await authAxios.get<OrganizationProfilesResponse>(`/marketing/organization-profiles/${id}`);
      return data;
    },
    [authAxios]
  );
  return useQuery(['OrganizationProfiles', id], () => getOrganizationProfiles(id), {
    refetchOnWindowFocus: false,
    enabled: !!id
  });
}

export function useAssignProfile(organizationId: string) {
  const { authAxios } = useAxios();
  const assignProfile = useCallback(
    async (profileId: string) => {
      const { data } = await authAxios.post<AssignedProfileRes>('/marketing/assign-profile', {
        organizationId,
        profileId
      });
      return data;
    },
    [authAxios, organizationId]
  );

  const assignProfileMutation = useMutation(assignProfile);
  return {
    assignProfile: assignProfileMutation.mutateAsync,
    isLoading: assignProfileMutation.isLoading
  };
}

export function useSaveAsProfile() {
  const { authAxios } = useAxios();
  const saveAsProfile = useCallback(
    async ({ name, ownership, settings, baseFlg }: MarketingSaveAsArgs) => {
      const response = await authAxios.post<Partial<MarketingProfileById>>('/marketing/profile', {
        name,
        owner: ownership.id,
        settings,
        baseFlg
      });

      return response.data;
    },
    [authAxios]
  );
  const saveAsProfileMutation = useMutation(saveAsProfile);
  return { saveAsProfile: saveAsProfileMutation.mutateAsync, isLoading: saveAsProfileMutation.isLoading };
}

export function useSaveProfile() {
  const { authAxios } = useAxios();
  const saveProfile = useCallback(
    async ({ name, id, settings, baseFlg }: MarketingMutationArgs) => {
      const res = await authAxios.put<Partial<MarketingProfileById>>('/marketing/profile', {
        name,
        id,
        baseFlg,
        settings
      });
      return res?.data;
    },
    [authAxios]
  );
  const saveProfileMutation = useMutation(saveProfile);

  return { saveProfile: saveProfileMutation.mutateAsync, isLoading: saveProfileMutation.isLoading };
}

export function useDeleteMarketingProfile() {
  const { authAxios } = useAxios();
  const deleteMarketingProfile = useCallback(
    async (id: string) => {
      const response = await authAxios.delete<DeleteMarketingProfileRes>('/marketing/profile', {
        params: {
          id: id
        }
      });
      return response.data;
    },
    [authAxios]
  );
  const deleteMarketingProfileMutation = useMutation(deleteMarketingProfile, {
    onSuccess: (data: DeleteMarketingProfileRes) => {
      if (data?.success) {
        toast.success('Profile deleted', {
          position: 'top-center'
        });
      } else {
        toast.error('An error occurred', {
          position: 'top-center'
        });
      }
    }
  });

  return {
    deleteMarketingProfile: deleteMarketingProfileMutation.mutateAsync,
    ...deleteMarketingProfileMutation
  };
}

export const useGetMarketingProfileById = ({
  id,
  onSuccess = () => null
}: {
  id: string;
  onSuccess?: (x: MarketingProfileById) => void;
}) => {
  const { authAxios } = useAxios();
  const getMarketingProfileById = useCallback(
    async (id: string) => {
      const { data } = await authAxios.get<MarketingProfileById>('/marketing/profile', {
        params: { id }
      });
      return data;
    },
    [authAxios]
  );
  return useQuery(['profileById', id], () => getMarketingProfileById(id), {
    refetchOnWindowFocus: false,
    enabled: !!id,
    onSuccess
  });
};

export function useGetAllMarketingProfiles(settings: GetAllMarketingProfilesSettings) {
  const { authAxios } = useAxios();
  const getAllMarketingProfiles = useCallback(
    async (params: GetAllMarketingProfilesSettings) => {
      const res: AxiosResponse<GetAllMarketingProfilesRes> = await authAxios.get<GetAllMarketingProfilesRes>(
        '/marketing/profiles',
        {
          params
        }
      );
      return res.data;
    },
    [authAxios]
  );

  return useInfiniteQuery(
    ['profiles', settings],
    ({ pageParam: endCursor }) => getAllMarketingProfiles({ endCursor, ...settings }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      getNextPageParam: (pageParam) => {
        return pageParam?.endCursor && pageParam.hasNextPage ? pageParam.endCursor : false;
      }
    }
  );
}

export const useUpdateMarketingProfile = () => {
  const { authAxios } = useAxios();

  const updateMarketingProfile = useCallback(
    async ({ id, name, baseFlg, settings }: MarketingMutationArgs) => {
      const res = await authAxios.put('/marketing/profile', {
        id,
        name,
        baseFlg,
        settings
      });
      return res.data;
    },
    [authAxios]
  );
  const updateMarketingProfileMutation = useMutation(updateMarketingProfile);
  return {
    updateMarketingProfile: updateMarketingProfileMutation.mutateAsync
  };
};

export const useMarketingProfileCache = () => {
  const queryClient = useQueryClient();

  const invalidateProfileCache = useCallback(
    (profileId: string) => queryClient.invalidateQueries([PROFILE_CACHE_KEY, profileId]),
    [queryClient]
  );

  const setProfileCache = useCallback(
    (profileId: string, dataToSet: any) => queryClient.setQueryData([PROFILE_CACHE_KEY, profileId], dataToSet),
    [queryClient]
  );

  return {
    invalidateProfileCache,
    setProfileCache
  };
};
